import styles from "./styles.module.scss";
import { Actions } from "./components/Actions";
import { Range } from "./components/Range";
import { List } from "./components/List";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { NewProduct } from "./components/NewProduct";
import TilesIcon from "@assets/icons/tiles.tsx";
import TableIcon from "@assets/icons/table.tsx";
import classNames from "classnames";
import { Table } from "./components/Table";
import { ProductsService } from "@services/amazon/products/products.service";
import { filterList, mapApiToItemData } from "./utils";
import { DataProps } from "./types";
import { BrandAsinSelect } from "@components/brand-asin-select";
import { useDashboardStore } from "@pages/Dashboard/store/dashboard.state";
import { Spin } from "antd";

type TabKey = "charts" | "table";

interface TabObject {
  code: TabKey;
  icon: React.ReactNode;
}

interface ProductsProps {
  fullSize?: boolean;
  useOnly?: TabKey;
  isCanvas?: boolean;
}

export const Products: React.FC<ProductsProps> = ({
  fullSize = false,
  useOnly,
  isCanvas = false,
}) => {
  const [data, setData] = useState<DataProps>({ data: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const { isAsinSelect, isBrandSelect, asinSelected, brandSelected } =
    useDashboardStore();

  const [addNewItem, setAddNewItem] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>(useOnly || "charts");
  const [selectedAsin, setSelectedAsin] = useState("");
  const productsService = new ProductsService();

  const [offset, setOffset] = useState(0);
  const limit = itemsPerPage;
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const fetchProducts = useCallback(async () => {
    if (isLoading || !hasMore) return;
    setIsLoading(true);
    const result = await productsService.getOwnProducts({ limit, offset });
    if (!result.length) {
      setHasMore(false);
      setIsLoading(false);
      return;
    }
    const mapped = result.map(mapApiToItemData);
    setData((prev) => ({ data: [...prev.data, ...mapped] }));
    setOffset((prev) => prev + limit);
    setIsLoading(false);
  }, [hasMore, isLoading, limit, offset]);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          setCurrentPage((prev) => prev + 1);
        }
      },
      { threshold: 1 },
    );
    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoading]);

  useEffect(() => {
    if (
      currentPage * itemsPerPage > data.data.length &&
      hasMore &&
      !isLoading
    ) {
      fetchProducts();
    }

    const loadedPages = Math.ceil(data.data.length / itemsPerPage) || 1;
    if (currentPage > loadedPages) {
      setCurrentPage(loadedPages);
    }
  }, [currentPage, data.data.length, hasMore, isLoading, fetchProducts]);

  const filteredData = filterList(
    data.data,
    isBrandSelect,
    isAsinSelect,
    brandSelected,
    asinSelected,
  );

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(0, currentPage * itemsPerPage);

  const handlePageChange = (page: number) => {
    const newPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(newPage);
  };

  const tabList: TabObject[] = [
    { code: "charts", icon: <TilesIcon color="red" /> },
    { code: "table", icon: <TableIcon /> },
  ];

  const handleCloseModal = () => setAddNewItem(false);
  const toggleTab = (code: TabKey) => setActiveTab(code);

  return (
    <div className={classNames(styles.container, fullSize && styles.fullSize)}>
      {!isCanvas && (
        <div className={styles.header}>
          <div className={styles.header__title}>
            <h2>Products</h2>
            {!useOnly && (
              <div className={classNames(styles["tabContainer__tab"])}>
                {tabList.map((item) => (
                  <button
                    key={item.code}
                    onClick={() => toggleTab(item.code)}
                    className={classNames(styles.element, {
                      [styles["element__active"]]: activeTab === item.code,
                    })}
                  >
                    {item.icon}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className={styles.actions}>
            <BrandAsinSelect />
            <Actions
              setAddNewItem={setAddNewItem}
              selectedAsin={selectedAsin}
              setSelectedAsin={setSelectedAsin}
            />
          </div>
        </div>
      )}
      {activeTab === "charts" ? (
        <>
          {!isCanvas && <Range data={data} />}
          <List
            data={paginatedData}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
          <div ref={loadMoreRef} style={{ height: "1px", width: "100%" }} />
          {isLoading && <Spin size="large" className={styles.loader} />}
        </>
      ) : (
        <Table data={data} />
      )}
      <NewProduct
        isOpen={addNewItem}
        onClose={handleCloseModal}
        data={data}
        setData={setData}
        selectedAsin={selectedAsin}
      />
    </div>
  );
};
